import { gql, useMutation, useQuery } from '@apollo/client'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ArtistDeleteFavoriteMemberMutation,
  ArtistDeleteFavoriteMemberMutationVariables,
  ArtistFavoriteMembersQuery,
  ArtistFavoriteMembersQueryVariables,
  ArtistUpdateFavoriteMemberSortMutation,
  ArtistUpdateFavoriteMemberSortMutationVariables,
  ArtistUpdateFavoriteMembersSettingsMutation,
  ArtistUpdateFavoriteMembersSettingsMutationVariables,
} from '@/clients/utoniq-core/schema'
import { useCurrentUser, useMessage, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { showMessage, showError } = useMessage()
  const { routerPages, router, generatePath } = useRouter()
  const { t } = useTranslation('consoleFavoriteMemberSettings')

  const { data, loading, refetch } = useQuery<
    ArtistFavoriteMembersQuery,
    ArtistFavoriteMembersQueryVariables
  >(ARTIST_FAVORITE_MEMBERS_QUERY, {
    skip: !user?.uid,
  })

  const [deleteFavoriteMember, { loading: deletingMember }] = useMutation<
    ArtistDeleteFavoriteMemberMutation,
    ArtistDeleteFavoriteMemberMutationVariables
  >(ARTIST_DELETE_FAVORITE_MEMBER_MUTATION)

  const [changeFavoriteMembers, { loading: changingSettings }] = useMutation<
    ArtistUpdateFavoriteMembersSettingsMutation,
    ArtistUpdateFavoriteMembersSettingsMutationVariables
  >(ARTIST_CHANGE_FAVORITE_MEMBERS_MUTATION)

  const [sortFavoriteMember, { loading: sortingMember }] = useMutation<
    ArtistUpdateFavoriteMemberSortMutation,
    ArtistUpdateFavoriteMemberSortMutationVariables
  >(ARTIST_SORT_FAVORITE_MEMBER_MUTATION)

  const favoriteMembers = useMemo(() => {
    return (
      data?.artistFavoriteMembers?.map(member => ({
        id: member.id,
        name: member.name,
        avatar: member.avatar?.uri ?? '',
        fanCount: member.fanCount,
      })) ?? []
    )
  }, [data?.artistFavoriteMembers])

  // 推しメンバー機能の有効/無効状態をクエリから取得
  const enableFavoriteMember =
    data?.artist?.settings?.features?.subscription?.enableFavoriteMembers ??
    false

  return {
    state: {
      artistId: user?.uid ?? '',
      favoriteMembers,
      enableFavoriteMember,
      loading,
      deletingMember,
      changingSettings,
      sortingMember,
    },
    actions: {
      onMove: async (prevIndex: number, nextIndex: number) => {
        // 推しメンバーの並び替えを実行
        const memberToMove = favoriteMembers[prevIndex]
        if (!memberToMove) {
          throw new Error(t('memberNotFoundError'))
        }

        await sortFavoriteMember({
          variables: {
            inputs: {
              favoriteMemberId: memberToMove.id,
              orderByNumber: nextIndex + 1,
            },
          },
        }).catch(error => {
          showError(error)
          throw error
        })

        await refetch()
        showMessage(t('updateOrderSuccess'), 'success')
      },
      onDelete: async (memberId: string) => {
        await deleteFavoriteMember({
          variables: {
            inputs: {
              favoriteMemberId: memberId,
            },
          },
        }).catch(error => {
          showError(error)
          throw error
        })

        await refetch()
      },
      onEnableSettings: async () => {
        await changeFavoriteMembers({
          variables: {
            inputs: {
              enableFavoriteMembers: true,
            },
          },
        }).catch(error => {
          showError(error)
          throw error
        })

        await refetch()
        showMessage(t('updateSettingsSuccess'), 'success')
      },
      onDisableSettings: async () => {
        await changeFavoriteMembers({
          variables: {
            inputs: {
              enableFavoriteMembers: false,
            },
          },
        }).catch(error => {
          showError(error)
          throw error
        })

        await refetch()
        showMessage(t('updateSettingsSuccess'), 'success')
      },
      onGoToCreate: () => {
        router.push(routerPages.ConsoleFavoriteMemberCreate)
      },
      onGoToEdit: (favoriteMemberId: string) => {
        router.push(
          generatePath(routerPages.ConsoleFavoriteMemberEdit, {
            favoriteMemberId,
          })
        )
      },
      onGoToUsers: (favoriteMemberId: string) => {
        router.push(
          generatePath(routerPages.ConsoleFans, {
            favoriteMemberId,
          })
        )
      },
    },
  }
})

const ARTIST_FAVORITE_MEMBERS_QUERY = gql`
  query ArtistFavoriteMembers {
    artist {
      settings {
        features {
          subscription {
            enableFavoriteMembers
          }
        }
      }
    }
    artistFavoriteMembers {
      id
      fanCount
      name
      avatar {
        uri
      }
      orderNumber
    }
  }
`

const ARTIST_DELETE_FAVORITE_MEMBER_MUTATION = gql`
  mutation ArtistDeleteFavoriteMember(
    $inputs: ArtistDeleteFavoriteMemberInput!
  ) {
    artistDeleteFavoriteMember(inputs: $inputs) {
      id
      name
      avatar {
        uri
      }
      orderNumber
    }
  }
`

const ARTIST_CHANGE_FAVORITE_MEMBERS_MUTATION = gql`
  mutation ArtistUpdateFavoriteMembersSettings(
    $inputs: ArtistUpdateFavoriteMembersSettingsInput!
  ) {
    artistUpdateFavoriteMembersSettings(inputs: $inputs) {
      settings {
        features {
          subscription {
            enableFavoriteMembers
          }
        }
      }
    }
  }
`

const ARTIST_SORT_FAVORITE_MEMBER_MUTATION = gql`
  mutation ArtistUpdateFavoriteMemberSort(
    $inputs: ArtistUpdateFavoriteMemberSortInput!
  ) {
    artistUpdateFavoriteMemberSort(inputs: $inputs) {
      id
      name
      avatar {
        uri
      }
      orderNumber
    }
  }
`
